import {
  AlertBanner,
  Box,
  Button,
  Icon,
  InputField,
  ListRow,
  ListRowGroup,
  Loader,
  SidePanel,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Tooltip,
  Typography,
} from "@saviynt/design-system";
import "@saviynt/tokens";
import React, { useCallback, useEffect, useRef, useState } from "react";
import SavContainer from "../../../common/SavContainer/SavContainer";
import "./VaultKeyList.css";
import { InterPoint } from "@saviynt/common";
import cx from "classnames";
import useFetchVaultKeyList from "../../../../hooks/use-fetchvaultkeylist";
import { getSecret, getVersionHistory, sendKeepalive } from "../../../../utilities/api/getDataFromApi";
import CopyButton from "../../../common/CopyButton/CopyButton";

/**
 * Component for displaying and searching vault keys with secret viewing
 * @param {Array} initialKeys - Initial list of vault keys to display
 * @returns {JSX.Element} VaultKeyList component
 */
export default function VaultKeyList({ initialKeys }) {
  // Key Search
  const [vaultKeys, setVaultKeys] = useState(initialKeys);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSidePanelLoading, setIsSidePanelLoading] = useState(false);

  // Secret Side Panel
  const [selectedKey, setSelectedKey] = useState({
    key_id: null,
    version: 0,
    created_ts: "",
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [originalSecret, setOriginalSecret] = useState("");
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [versionHistory, setVersionHistory] = useState([]);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [isVersionHistoryLoading, setIsVersionHistoryLoading] = useState(false);
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(-1);

  // Error handling
  const [errorAlert, setErrorAlert] = useState(null);

  const sidepanelRef = useRef(null);
  const privateKeyTimeoutRef = useRef(null);
  const listContainerRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const hasReachedBottomRef = useRef(false);

  const [isCopyButtonClicked, setIsCopyButtonClicked] = useState(false);

  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    debouncedSearch,
  } = useFetchVaultKeyList(searchTerm, setVaultKeys);

  // Don't override with initialKeys after hook starts managing state
  useEffect(() => {
    if (initialKeys.length > 0 && vaultKeys.length === 0) {
      setVaultKeys(initialKeys);
    }
  }, [initialKeys]);

  // Send keepalive on user activity (throttled to once per 60s)
  const lastKeepaliveRef = useRef(0);

  // Fire a keepalive IMMEDIATELY on a deliberate secret interaction — view a
  // secret, toggle its visibility (eye icon), copy it, show/hide version history,
  // select a version — so the session is extended right away, not waiting on the
  // throttled global activity listener. Updates lastKeepaliveRef so the global
  // listener doesn't double-fire right after.
  const keepAliveNow = useCallback(() => {
    lastKeepaliveRef.current = Date.now();
    sendKeepalive();
  }, []);
  useEffect(() => {
    const KEEPALIVE_THROTTLE_MS = 60000;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastKeepaliveRef.current > KEEPALIVE_THROTTLE_MS) {
        lastKeepaliveRef.current = now;
        sendKeepalive();
      }
    };
    window.addEventListener("click", handleActivity);
    window.addEventListener("keydown", handleActivity);
    window.addEventListener("scroll", handleActivity, true);
    return () => {
      window.removeEventListener("click", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("scroll", handleActivity, true);
    };
  }, []);

  useEffect(() => {
    if (isSecretVisible) {
      privateKeyTimeoutRef.current = setTimeout(() => {
        setIsSecretVisible(false);
      }, 5000);
    } else {
      clearTimeout(privateKeyTimeoutRef.current);
    }

    return () => {
      clearTimeout(privateKeyTimeoutRef.current);
    };
  }, [isSecretVisible]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    const container = listContainerRef.current;
    if (!container) return;

    const isBottom =
      Math.floor(container.scrollHeight - container.scrollTop) - 5 <= container.clientHeight;
    const isScrollingDown = container.scrollTop > lastScrollTopRef.current;

    if (isBottom && isScrollingDown && !hasReachedBottomRef.current && hasNextPage && !isFetchingNextPage) {
      hasReachedBottomRef.current = true;
      fetchNextPage().then(() => {
        hasReachedBottomRef.current = false;
      });
    }

    lastScrollTopRef.current = container.scrollTop;
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const container = listContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    debouncedSearch();
  }, [searchTerm]);

  /**
   * Fetches secret for a vault key
   * @param {string} keyId - The key identifier
   * @param {number} version - Optional version number
   */
  const getVaultSecret = async (keyId, version) => {
    keepAliveNow();
    setIsSidePanelLoading(true);
    try {
      const response = await getSecret(keyId, version);
      if (response?.status >= 400) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP ${response.status}`);
      }
      setSecret(response?.secret);
      if (!version) {
        setOriginalSecret(response?.secret);
      }
      setErrorAlert(null);
    } catch (error) {
      const description = error?.message
        ? `Server error: ${error.message}`
        : "Server error occurred";
      setErrorAlert({
        title: "Fetching Vault secret failed",
        description,
      });
    } finally {
      setIsSidePanelLoading(false);
    }
  };

  /**
   * Fetches version history for a vault key
   * @param {string} keyId - The key identifier
   */
  const fetchVersionHistory = async (keyId) => {
    setIsVersionHistoryLoading(true);
    setSelectedVersionIndex(-1);
    try {
      const response = await getVersionHistory(keyId);
      setVersionHistory(response || []);
      setShowVersionHistory(true);
    } finally {
      setIsVersionHistoryLoading(false);
    }
  };

  /**
   * Toggles version history visibility
   */
  const toggleVersionHistory = () => {
    keepAliveNow();
    if (showVersionHistory) {
      if (isSecretVisible) {
        setIsSecretVisible(false);
        clearTimeout(privateKeyTimeoutRef.current);
      }
      setShowVersionHistory(false);
      setSecret(originalSecret);
      setSelectedVersionIndex(-1);
    } else {
      if (isSecretVisible) {
        setIsSecretVisible(false);
        clearTimeout(privateKeyTimeoutRef.current);
      }
      fetchVersionHistory(selectedKey.key_id);
    }
  };

  /**
   * Renders the center content for a list row
   * @param {Object} key - Vault key object
   * @returns {JSX.Element} Formatted key information
   */
  const centerContent = (key) => {
    const dateString = new Date(key.created_ts).toLocaleDateString();
    return (
      <Box className="VaultKeyList-listRowCenterContent">
        <div className="VaultKeyList-listRow--title">
          <Typography kind="h3">{key.key_id}</Typography>
        </div>
        <div className="VaultKeyList-listRow--description">
          <Box className="VaultKeyList-listRow--descriptionField">
            <Typography kind="h4">Version: </Typography>
            <Typography kind="body2">{key.version}</Typography>
          </Box>
          <InterPoint font="body3" />
          <Box className="VaultKeyList-listRow--descriptionField">
            <Typography kind="h4">Date Created: </Typography>
            <Typography kind="body2">{dateString}</Typography>
          </Box>
        </div>
      </Box>
    );
  };

  /**
   * Handles version row selection
   * @param {number} index - Row index
   * @param {Object} version - Version object
   */
  const handleVersionSelect = (index, version) => {
    keepAliveNow();
    if (isSecretVisible) {
      setIsSecretVisible(false);
      clearTimeout(privateKeyTimeoutRef.current);
    }
    setSelectedVersionIndex(index);
    if(!selectedKey.key_id) return;
    getVaultSecret(selectedKey.key_id, version?.version);
  };

  /**
   * Renders version history table
   * @returns {JSX.Element} Version history table
   */
  const renderVersionHistory = () => {
    const columns = [
      { key: "version", label: "Version", width: "10rem" },
      { key: "created_ts", label: "Created Date", width: "100%", minWidth: "10rem" }
    ];

    return (
        <Box className="VaultKeyList-versionHistoryContainer">
          <Typography kind="h3">Version History</Typography>
          <Box className="VaultKeyList-versionHistoryTable">
            <Table>
              <TableHead columns={columns} isDndDisabled isVirtualizationEnabled={false} className="VaultKeyList-versionHistoryTableHead" />
              <TableBody>
                {versionHistory.map((v, index) => {
                  const keyStr = `${selectedKey.key_id || 'key'}:${v.version}`;
                  const isSelected = selectedVersionIndex === index;
                  return (
                      <TableRow
                          key={keyStr}
                          dataTestId={`row-${v.version}`}
                          className={isSelected ? "VaultKeyList-versionRow--selected" : null}
                      >
                        <TableCell dataTestId={`cell-version-${v.version}`}>
                          <Button
                            kind="ghost"
                            size="small"
                            onClick={() => handleVersionSelect(index, v)}
                            className="VaultKeyList-versionCellButton"
                          >
                            {v.version}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <Button
                            kind="ghost"
                            size="small"
                            onClick={() => handleVersionSelect(index, v)}
                            className="VaultKeyList-versionCellButton"
                          >
                            {new Date(v.created_ts).toLocaleDateString()}
                          </Button>
                        </TableCell>
                      </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>
    );
  };

  /**
   * Renders secret details in side panel
   * @returns {JSX.Element} Secret input field or loader
   */
  const getSecretDetails = () =>
    isSidePanelLoading ? (
      <Box className="VaultKeyList-sidePanelLoader">
        <Loader kind="dots" format="inline" color="info" size="medium" />
      </Box>
    ) : (
      <Box className="VaultKeyList-secretContainer">
        <Box className="VaultKeyList-secretInputSection">
          <InputField
            name="secret"
            placeholder="Secret"
            label={
              <Typography kind="label" htmlFor="Read-only">
                Secret:
              </Typography>
            }
            isReadOnly
            suffixIsToggle={isSecretVisible}
            value={secret}
            setValue={setSecret}
            kind="multiline"
            className={
                isSecretVisible
                    ? "VaultKeyList-secretInput"
                    : "VaultKeyList-secretInput--masked"
            }
            classNames={{
              inputContainer: "VaultKeyList-secretInputContainer",
            }}
            textAreaMaxHeight="40rem"
          />
          <div className="VaultKeyList-secretButtonsContainer">
            <Tooltip
              text={isSecretVisible ? "Hide Password" : "Show Password"}
              placement="top"
              closeDelayAfterClick={5000}
              trigger={
                <Button
                  type="button"
                  kind="outlined"
                  size="large"
                  leftIcon={
                    <Icon
                      kind={isSecretVisible ? "visibilityOff" : "visibilityOn"}
                      size="medium"
                      color="neutral-100"
                    />
                  }
                  onClick={() => {
                    keepAliveNow();
                    setIsSecretVisible(!isSecretVisible);
                  }}
                />
              }
            />
            <CopyButton
              handleCopyOnClick={handleCopyOnClick}
              isCopyButtonClicked={isCopyButtonClicked}
              copyButtonValue={secret}
            />
          </div>
        </Box>
        {selectedKey.version > 1 && (
          <Box className="VaultKeyList-versionHistoryButtonContainer">
            <Button
              type="button"
              kind="filled"
              size="medium"
              onClick={toggleVersionHistory}
              isLoading={isVersionHistoryLoading}
              dataTestId="version-history-button"
            >
              {showVersionHistory ? "Hide Version History" : "Show Version History"}
            </Button>
          </Box>
        )}
        {showVersionHistory && (
          <Box className="VaultKeyList-versionHistorySection">
            {isVersionHistoryLoading ? (
              <Box className="VaultKeyList-versionHistoryLoader">
                <Loader kind="dots" format="inline" color="info" size="medium" />
              </Box>
            ) : (
              renderVersionHistory()
            )}
          </Box>
        )}
      </Box>
    );

  const resultsWrapperClasses = cx(
    "VaultKeyList-ListRow-sidePanelListRowGroupWrapper",
    isSidePanelOpen &&
      "VaultKeyList-ListRow-sidePanelListRowGroupWrapper--isSidePanelOpen",
  );

  const handleCopyOnClick = async (value) => {
    keepAliveNow();
    setIsCopyButtonClicked(true);
    await navigator.clipboard.writeText(value);
  };

  return (
    <SavContainer>
      <Box className="VaultKeyList">
        {errorAlert && (
          <AlertBanner
            colorTheme="Critical"
            title={errorAlert.title}
            description={errorAlert.description}
            isVisible={true}
            onCancel={() => setErrorAlert(null)}
            isColonVisible={false}
            shouldAutoDismiss
            autoDismissDelay={5000}
          />
        )}
        <Typography kind="h1" className="VaultKeyList-title">
          Enter Key to Search
        </Typography>
        <Box className="VaultKeyList-searchContainer">
          <InputField
            name="basic"
            placeholder="Search Key"
            kind="search"
            prefixIcon={
              <Icon kind="Search" color="--color-foreground-neutral-subtle" />
            }
            value={searchTerm}
            setValue={(v) => {
              setSearchTerm(v);
              // Typing in the filter is active user interaction — fire a throttled
              // keepalive so the session extends even if no BE API call fires yet
              // (the debounced search hasn't triggered). Uses the same 60s throttle
              // as the global activity listener to avoid spamming on every keystroke.
              const now = Date.now();
              if (now - lastKeepaliveRef.current > 60000) {
                lastKeepaliveRef.current = now;
                sendKeepalive();
              }
            }}
            resetValue={() => {
              setIsSidePanelOpen(false);
            }}
            suffixButtonType={null}
          />
        </Box>
        <Box className="VaultKeyList-results">
          <Box className={resultsWrapperClasses} ref={listContainerRef}>
            {isFetching && !isFetchingNextPage ? (
              <Box className="VaultKeyList-loader">
                <Loader
                  kind="dots"
                  format="inline"
                  color="info"
                  size="medium"
                />
              </Box>
            ) : (
              <>
                <ListRowGroup>
                  {vaultKeys.length > 0 ? (
                    vaultKeys.map((vaultKey) => {
                      const isRowSelected =
                        selectedKey.key_id === vaultKey.key_id && isSidePanelOpen;

                      return (
                        <ListRow
                          key={vaultKey.key_id}
                          className="VaultKeyList-listRow"
                          centerContent={centerContent(vaultKey)}
                          handleRowClick={() => {
                            setSelectedKey(vaultKey);
                            setIsSidePanelOpen(true);
                            setIsSecretVisible(false);
                            setShowVersionHistory(false);
                            setVersionHistory([]);
                            getVaultSecret(vaultKey.key_id);
                          }}
                          isRowDetailsOpen={isRowSelected}
                          isListItem={false}
                          connectorDistance="3.125rem"
                          isRowConnectTransitionEnabled
                          isCheckboxDisabled
                          isDisabled={isRowSelected}
                        />
                      );
                    })
                  ) : (
                    <Typography kind="h3">
                      No keys were found for the inputted search value.
                    </Typography>
                  )}
                </ListRowGroup>
                {isFetchingNextPage && (
                  <Box className="VaultKeyList-loader">
                    <Loader
                      kind="loop"
                      format="main"
                      color="brand"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          <SidePanel
            ref={sidepanelRef}
            header={{
              title: selectedKey.key_id,
              headerIcon: <Icon kind="Applications" size="xxLarge" />,
            }}
            layout={getSecretDetails()}
            isOpen={isSidePanelOpen}
            onClose={() => setIsSidePanelOpen(false)}
            className="VaultKeyList-ListRow-sidePanel"
            layoutClassName="VaultKeyList-ListRow-sidePanelLayout"
          />
        </Box>
      </Box>
    </SavContainer>
  );
}
